
/* === image-handoff.js === */
/**
 * Image Handoff — Automatic scroll-driven image transition
 *
 * Detects when two adjacent sections share the same image (same Shopify CDN source)
 * and creates a smooth flying animation between them on scroll.
 *
 * Features: Multiple path types (bezier, arc, direct), dynamic shadow, various easings
 */
class ImageHandoff {
  constructor() {
    this.pairs = [];
    this.activePairs = new Set();
    this.ticking = false;
    this.isActive = false;
    this.resizeObserver = null;
    this.visibilityObserver = null;
    this._sectionTimer = null;
    this._sectionVis = new Map();

    // Respect reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this._setupSectionEvents();

    if (window.innerWidth < 750) {
      this._watchBreakpoint();
      return;
    }

    this._scheduleInit();
  }

  // --- Init ---

  _scheduleInit() {
    if (document.readyState === 'complete') {
      this.init();
    } else {
      window.addEventListener('load', () => this.init(), { once: true });
    }
  }

  init() {
    if (this.isActive) return;
    this.isActive = true;

    this.collect();
    if (this.pairs.length === 0) {
      this.isActive = false;
      return;
    }

    this._setupResizeObserver();
    this._setupImageLoads();
    this._setupVisibility();
    this._startScrollLoop();
    this._watchBreakpoint();
    this._update();
  }

  // --- Image matching ---

  collect() {
    this._cleanup();

    const sections = document.querySelectorAll('.shopify-section');
    const list = [...sections];

    for (let i = 0; i < list.length - 1; i++) {
      try {
        const pair = this._matchSections(list[i], list[i + 1]);
        if (pair) this.pairs.push(pair);
      } catch (e) {
        // skip broken pair
      }
    }

    if (this.pairs.length > 0) this._recalcAllRects();
  }

  _matchSections(sectionA, sectionB) {
    const getAttr = (section, attr) => {
      return section.dataset[attr] || section.firstElementChild?.dataset[attr] || null;
    };

    const isDisabledA = getAttr(sectionA, 'handoffDisabled') === 'true';
    const isDisabledB = getAttr(sectionB, 'handoffDisabled') === 'true';

    if (isDisabledA || isDisabledB) return null;

    const imgA = this._findPrimaryImage(sectionA);
    const imgB = this._findPrimaryImage(sectionB);
    if (!imgA || !imgB) return null;
    if (!this._sameImage(imgA, imgB)) return null;

    // Validate dimensions
    const ra = imgA.getBoundingClientRect();
    const rb = imgB.getBoundingClientRect();
    if (ra.width < 50 || ra.height < 50 || rb.width < 50 || rb.height < 50) return null;

    // Get settings from target section (where the image flies to)
    const path = getAttr(sectionB, 'handoffPath') || 'bezier';
    const easing = getAttr(sectionB, 'handoffEasing') || 'smooth';

    return {
      sourceImg: imgA,
      targetImg: imgB,
      sourceSection: sectionA,
      targetSection: sectionB,
      clone: null,
      sourceRect: null,
      targetRect: null,
      sourceBR: [0, 0, 0, 0],
      targetBR: [0, 0, 0, 0],
      state: 'idle',
      isNearViewport: false,
      path,
      easing,
    };
  }

  _findPrimaryImage(section) {
    // Look inside [data-media-container] for significant images
    const containers = section.querySelectorAll('[data-media-container]');
    for (const c of containers) {
      const img = c.querySelector('img');
      if (img && img.src && this._isSignificant(img)) return img;
    }

    // Fallback: any large .media img
    const imgs = section.querySelectorAll('.media img');
    for (const img of imgs) {
      if (img.src && this._isSignificant(img)) return img;
    }

    return null;
  }

  _isSignificant(img) {
    const r = img.getBoundingClientRect();
    return r.width > 150 && r.height > 100;
  }

  _sameImage(a, b) {
    const sa = this._normalizeUrl(a.src);
    const sb = this._normalizeUrl(b.src);
    return sa && sb && sa === sb;
  }

  _normalizeUrl(url) {
    if (!url) return '';
    try {
      const u = new URL(url, location.origin);
      // Remove all CDN transform params
      const transformParams = ['width', 'height', 'crop', 'pad_color', 'format', 'quality'];
      transformParams.forEach(k => u.searchParams.delete(k));
      // Normalize path: remove size, crop, and density suffixes
      let path = u.pathname
        .replace(/_\d+x\d*\./, '.')           // _800x600. or _800x.
        .replace(/_x\d+\./, '.')               // _x600.
        .replace(/_crop_[a-z]+\./, '.')        // _crop_center.
        .replace(/@\dx\./, '.');               // @2x.
      const v = u.searchParams.get('v');
      return path + (v ? '?v=' + v : '');
    } catch {
      return url;
    }
  }

  _createClone(img) {
    const clone = document.createElement('div');
    clone.className = 'handoff-clone';
    clone.setAttribute('aria-hidden', 'true');
    clone.style.display = 'none';

    const copy = document.createElement('img');
    copy.src = img.currentSrc || img.src;
    if (img.srcset) copy.srcset = img.srcset;
    if (img.sizes) copy.sizes = img.sizes;
    copy.alt = '';
    copy.loading = 'eager';

    // Preserve object-fit and object-position
    const style = getComputedStyle(img);
    copy.style.objectFit = style.objectFit || 'cover';
    copy.style.objectPosition = style.objectPosition || 'center';

    clone.appendChild(copy);

    return clone;
  }

  // --- Safe element access ---

  _safeParent(el) {
    return el?.parentElement;
  }

  // --- Rects ---

  _recalcAllRects() {
    const scrollY = window.scrollY;

    const reads = this.pairs.map(p => ({
      p,
      sr: p.sourceImg.getBoundingClientRect(),
      tr: p.targetImg.getBoundingClientRect(),
      sbr: this._parseBR(p.sourceImg),
      tbr: this._parseBR(p.targetImg),
    }));

    for (const { p, sr, tr, sbr, tbr } of reads) {
      if (sr.width === 0 || tr.width === 0) {
        p.sourceRect = null;
        p.targetRect = null;
        continue;
      }
      p.sourceRect = { top: sr.top + scrollY, left: sr.left, width: sr.width, height: sr.height };
      p.targetRect = { top: tr.top + scrollY, left: tr.left, width: tr.width, height: tr.height };
      p.sourceBR = sbr;
      p.targetBR = tbr;
    }
  }

  _parseBR(el) {
    const s = getComputedStyle(el);
    return [
      parseFloat(s.borderTopLeftRadius) || 0,
      parseFloat(s.borderTopRightRadius) || 0,
      parseFloat(s.borderBottomRightRadius) || 0,
      parseFloat(s.borderBottomLeftRadius) || 0,
    ];
  }

  _setupResizeObserver() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    let t;
    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(t);
      t = setTimeout(() => this._recalcAllRects(), 100);
    });
    for (const p of this.pairs) {
      this.resizeObserver.observe(p.sourceImg);
      this.resizeObserver.observe(p.targetImg);
    }
  }

  _setupImageLoads() {
    for (const p of this.pairs) {
      for (const img of [p.sourceImg, p.targetImg]) {
        if (!img.complete) {
          img.addEventListener('load', () => this._recalcAllRects(), { once: true });
        }
      }
    }
  }

  // --- Visibility gate ---

  _setupVisibility() {
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
    this._sectionVis.clear();

    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const e of entries) this._sectionVis.set(e.target, e.isIntersecting);
        this._rebuildActive();
      },
      { rootMargin: '100% 0px 100% 0px' }
    );

    for (const p of this.pairs) {
      this.visibilityObserver.observe(p.sourceSection);
      this.visibilityObserver.observe(p.targetSection);
    }
  }

  _rebuildActive() {
    const prev = new Set(this.activePairs);
    this.activePairs.clear();

    for (const p of this.pairs) {
      p.isNearViewport =
        (this._sectionVis.get(p.sourceSection) || false) ||
        (this._sectionVis.get(p.targetSection) || false);
      if (p.isNearViewport) this.activePairs.add(p);
    }

    // Force-idle pairs that left viewport while not idle
    for (const p of prev) {
      if (!this.activePairs.has(p) && p.state !== 'idle') this._setIdle(p);
    }
  }

  // --- Scroll loop ---

  _startScrollLoop() {
    this._onScroll = () => {
      if (this.ticking || document.hidden) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this._update();
        this.ticking = false;
      });
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });

    this._onVisChange = () => {
      if (!document.hidden) {
        this.ticking = false;
        this._recalcAllRects();
        this._update();
      }
    };
    document.addEventListener('visibilitychange', this._onVisChange);
  }

  _update() {
    const scrollY = window.scrollY;
    const vh = window.innerHeight;

    for (const p of this.activePairs) {
      if (!p.sourceRect || !p.targetRect) continue;

      // Improved range for smoother bidirectional transition
      const start = p.sourceRect.top + p.sourceRect.height * 0.7 - vh;
      const end = p.targetRect.top + p.targetRect.height * 0.3 - vh * 0.7;
      const range = end - start;
      if (range <= 0) continue;

      const progress = Math.max(0, Math.min(1, (scrollY - start) / range));

      if (progress <= 0.02) this._setIdle(p);
      else if (progress >= 0.98) this._setLanded(p);
      else this._setFlying(p, progress);
    }
  }

  // --- State machine ---

  _setIdle(p) {
    if (p.state === 'idle') return;
    p.state = 'idle';

    if (p.clone) p.clone.style.display = 'none';
    p.sourceImg.style.opacity = '';

    const parent = this._safeParent(p.targetImg);
    if (parent) {
      parent.classList.remove('handoff-incoming', 'handoff-landed');
    }

    this._removeReceived(p);
  }

  _setLanded(p) {
    if (p.state === 'landed') return;
    p.state = 'landed';

    if (p.clone) p.clone.style.display = 'none';
    p.sourceImg.style.opacity = '0';

    const parent = this._safeParent(p.targetImg);
    if (!parent) return;

    parent.classList.remove('handoff-incoming');
    parent.classList.add('handoff-landed');

    // Place a copy of the source image in the target
    if (!parent.querySelector('.handoff-received')) {
      if (p.clone) {
        const cloneImg = p.clone.querySelector('img');
        if (cloneImg) {
          const received = cloneImg.cloneNode(true);
          received.className = 'handoff-received';
          parent.appendChild(received);
        }
      }
    }
  }

  _setFlying(p, progress) {
    const prev = p.state;
    p.state = 'flying';

    // Lazy create clone on first fly
    if (!p.clone) {
      p.clone = this._createClone(p.sourceImg);
      document.body.appendChild(p.clone);
      // Force reflow before showing to prevent flash
      p.clone.offsetHeight;
    }

    const parent = this._safeParent(p.targetImg);

    if (prev === 'idle') {
      p.sourceImg.style.opacity = '0';
      if (parent) parent.classList.add('handoff-incoming');
      p.clone.style.display = 'block';
    } else if (prev === 'landed') {
      if (parent) {
        parent.classList.remove('handoff-landed');
        parent.classList.add('handoff-incoming');
      }
      p.clone.style.display = 'block';
      this._removeReceived(p);
    }

    const t = this._ease(progress, p.easing);

    const sr = p.sourceRect;
    const tr = p.targetRect;
    const scrollY = window.scrollY;

    // Calculate position based on path type
    const { x, y } = this._calcPosition(sr, tr, scrollY, t, p.path);
    const w = sr.width + (tr.width - sr.width) * t;
    const h = sr.height + (tr.height - sr.height) * t;

    const br = this._lerpBR(p.sourceBR, p.targetBR, t);

    // Dynamic shadow - peaks at middle of flight
    const elevation = Math.sin(t * Math.PI);
    const shadowY = 8 + elevation * 24;
    const shadowBlur = 16 + elevation * 32;
    const shadowOpacity = 0.1 + elevation * 0.15;

    const c = p.clone;
    c.style.transform = `translate3d(${x}px,${y}px,0)`;
    c.style.width = w + 'px';
    c.style.height = h + 'px';
    c.style.borderRadius = `${br[0]}px ${br[1]}px ${br[2]}px ${br[3]}px`;
    c.style.setProperty('--ho-sy', `${shadowY}px`);
    c.style.setProperty('--ho-sb', `${shadowBlur}px`);
    c.style.setProperty('--ho-so', shadowOpacity);
  }

  // --- Math ---

  _calcPosition(sr, tr, scrollY, t, path) {
    const srcY = sr.top - scrollY;
    const tgtY = tr.top - scrollY;

    switch (path) {
      case 'bezier': {
        // Cubic bezier with control points for natural curve
        const cp1y = srcY + (tgtY - srcY) * 0.3;
        const cp2y = srcY + (tgtY - srcY) * 0.7;

        const mt = 1 - t;
        const mt2 = mt * mt;
        const mt3 = mt2 * mt;
        const t2 = t * t;
        const t3 = t2 * t;

        return {
          x: mt3 * sr.left + 3 * mt2 * t * sr.left + 3 * mt * t2 * tr.left + t3 * tr.left,
          y: mt3 * srcY + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * tgtY
        };
      }

      case 'vertical_first': {
        // First 60% = vertical movement, last 40% = horizontal
        const vertT = Math.min(1, t / 0.6);
        const horzT = Math.max(0, (t - 0.6) / 0.4);
        return {
          x: sr.left + (tr.left - sr.left) * this._easeOut(horzT),
          y: srcY + (tgtY - srcY) * this._easeOut(vertT)
        };
      }

      case 'arc': {
        // Parabolic arc - rises then falls
        const arcHeight = Math.abs(tr.left - sr.left) * 0.2 + 30;
        const arcOffset = 4 * arcHeight * t * (1 - t);
        return {
          x: sr.left + (tr.left - sr.left) * t,
          y: srcY + (tgtY - srcY) * t - arcOffset
        };
      }

      case 'direct':
      default:
        return {
          x: sr.left + (tr.left - sr.left) * t,
          y: srcY + (tgtY - srcY) * t
        };
    }
  }

  _ease(t, easing = 'smooth') {
    switch (easing) {
      case 'bounce': {
        // More pronounced bounce with 2 rebounds
        if (t < 0.7) return (t / 0.7) * (t / 0.7);
        const bounceT = (t - 0.7) / 0.3;
        return 1 + Math.sin(bounceT * Math.PI * 2) * 0.08 * (1 - bounceT);
      }

      case 'elastic': {
        // Rubber band effect
        if (t === 0 || t === 1) return t;
        return Math.pow(2, -10 * t) * Math.sin((t - 0.075) * (2 * Math.PI) / 0.3) + 1;
      }

      case 'expo': {
        // Exponential - dramatic
        return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
      }

      case 'back': {
        // Slight overshoot then settle
        const c1 = 1.70158;
        const c3 = c1 + 1;
        return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
      }

      case 'sharp': {
        // Improved: ease-in-out cubic for smoother feel
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      }

      case 'smooth':
      default:
        if (t < 0.5) return 2 * t * t;
        return 1 - 2 * Math.pow(1 - t, 2);
    }
  }

  _easeOut(t) {
    return 1 - Math.pow(1 - t, 2);
  }

  _lerpBR(a, b, t) {
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t, a[3] + (b[3] - a[3]) * t];
  }

  _removeReceived(p) {
    const parent = this._safeParent(p.targetImg);
    if (!parent) return;
    const r = parent.querySelector('.handoff-received');
    if (r) r.remove();
  }

  // --- Breakpoint ---

  _watchBreakpoint() {
    if (this._mql) return;
    this._mql = window.matchMedia('(min-width: 750px)');
    this._mqlHandler = (e) => {
      if (e.matches && !this.isActive) this._scheduleInit();
      else if (!e.matches && this.isActive) this._teardown();
    };
    this._mql.addEventListener('change', this._mqlHandler);
  }

  // --- Section events ---

  _setupSectionEvents() {
    if (this._onSectionLoad) return;

    this._onSectionLoad = () => {
      clearTimeout(this._sectionTimer);
      this._sectionTimer = setTimeout(() => { this._teardown(); this.init(); }, 150);
    };

    this._onSectionUnload = (e) => {
      if (this.pairs.some(p => p.sourceSection === e.target || p.targetSection === e.target)) {
        clearTimeout(this._sectionTimer);
        this._sectionTimer = setTimeout(() => { this._teardown(); this.init(); }, 150);
      }
    };

    document.addEventListener('shopify:section:load', this._onSectionLoad);
    document.addEventListener('shopify:section:reorder', this._onSectionLoad);
    document.addEventListener('shopify:section:unload', this._onSectionUnload);

    this._onPageLoaded = () => { this._teardown(); this.init(); };
    document.addEventListener('page:loaded', this._onPageLoaded);
  }

  // --- Lifecycle ---

  _teardown() {
    this._cleanup();
    if (this._onScroll) { window.removeEventListener('scroll', this._onScroll); this._onScroll = null; }
    if (this._onVisChange) { document.removeEventListener('visibilitychange', this._onVisChange); this._onVisChange = null; }
    if (this.resizeObserver) { this.resizeObserver.disconnect(); this.resizeObserver = null; }
    if (this.visibilityObserver) { this.visibilityObserver.disconnect(); this.visibilityObserver = null; }
    this._sectionVis.clear();
    clearTimeout(this._sectionTimer);
    this.ticking = false;
    this.isActive = false;
  }

  _cleanup() {
    for (const p of this.pairs) {
      try {
        if (p.clone) p.clone.remove();
        p.sourceImg.style.opacity = '';
        const parent = this._safeParent(p.targetImg);
        if (parent) {
          parent.classList.remove('handoff-incoming', 'handoff-landed');
        }
        this._removeReceived(p);
      } catch (e) {}
    }
    this.pairs = [];
    this.activePairs.clear();
  }

  destroy() {
    this._teardown();
    if (this._mql) { this._mql.removeEventListener('change', this._mqlHandler); this._mql = null; }
    if (this._onSectionLoad) {
      document.removeEventListener('shopify:section:load', this._onSectionLoad);
      document.removeEventListener('shopify:section:reorder', this._onSectionLoad);
    }
    if (this._onSectionUnload) document.removeEventListener('shopify:section:unload', this._onSectionUnload);
    if (this._onPageLoaded) document.removeEventListener('page:loaded', this._onPageLoaded);
  }
}

if (!window.ImageHandoff) {
  window.ImageHandoff = new ImageHandoff();
}

